import type { AxiosError, AxiosResponse, AxiosResponseHeaders } from 'axios';

import createBaseAxiosInstance, {
  requestHandler,
  responseErrorHandler,
  responseHandler,
} from './createBaseAxiosInstance';
import type { ApiError } from './error';
import type { BaseApiResponse } from './type';

type AxiosInterceptorUse<T = any, E = any> = (
  onFulfilled?: ((value: T) => T | Promise<T>) | null,
  onRejected?: ((error: E) => any) | null,
) => number;

const createAxiosInstance = (param?: {
  requestErrorHandler?: Parameters<AxiosInterceptorUse>[1];
  requestHandler?: Parameters<AxiosInterceptorUse>[0];
  responseErrorHandler?: Parameters<
    AxiosInterceptorUse<
      AxiosResponse<any, any, {}>,
      ApiError<any, AxiosResponseHeaders>
    >
  >[1];
  responseHandler?: Parameters<
    AxiosInterceptorUse<AxiosResponse<any, any, {}>>
  >[0];
}) => {
  const baseInstance = createBaseAxiosInstance();

  baseInstance.interceptors.request.use(config => {
    const defaultConfig = requestHandler(config);

    return typeof param?.requestHandler === 'function'
      ? param.requestHandler(defaultConfig)
      : defaultConfig;
  }, param?.requestErrorHandler);

  baseInstance.interceptors.response.use(
    res => {
      const defaultRes = responseHandler(res);

      return typeof param?.responseHandler === 'function'
        ? param.responseHandler(defaultRes)
        : defaultRes;
    },
    (error: AxiosError<BaseApiResponse<any>>) => {
      if (typeof param?.responseErrorHandler === 'function') {
        try {
          responseErrorHandler(error);
        } catch (error: any) {
          throw param.responseErrorHandler(
            error as ApiError<any, AxiosResponseHeaders>,
          );
        }
      } else {
        responseErrorHandler(error);
      }
    },
  );

  return baseInstance;
};

export default createAxiosInstance;
